import React, { useState, useEffect, useReducer, useContext, useRef, useCallback } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import useWhatsApps from "../../hooks/useWhatsApps";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets, { clearTicketsCache } from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TICKETS_REFRESH_EVENT } from "../../utils/ticketRefresh";
import { groupInternalTickets } from "../../utils/internalConversations";
import { isTicketSnoozed } from "../../utils/ticketSnooze";
import {
	onPendingMessage,
	resolvePendingMessage,
	withPendingMessagePreview,
} from "../../services/pendingMessages";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: theme.palette.text.primary,
		zIndex: 2,
		backgroundColor: theme.palette.background.paper,
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: theme.palette.text.secondary,
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		color: theme.palette.text.primary,
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	attentionHeader: {
		fontWeight: 600,
		fontSize: "13px",
		lineHeight: "32px",
		zIndex: 3,
		color: theme.palette.type === "dark" ? "#ffffff" : theme.palette.error.main,
		backgroundColor: theme.palette.type === "dark" ? "#753b3b" : "#fff3f3",
	},

	attendedHeader: {
		fontWeight: 600,
		fontSize: "13px",
		lineHeight: "32px",
		zIndex: 3,
		color: theme.palette.type === "dark" ? "#ffffff" : theme.palette.success.dark,
		backgroundColor: theme.palette.type === "dark" ? "#3f5d40" : "#edf7ee",
	},

	emptyAttentionTray: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(1, 2),
		color: theme.palette.text.disabled,
	},

	emptyAttentionText: {
		fontSize: "13px",
	},
}));

const ACTIVE_ATTENTION_STATUSES = ["unseen", "seen_pending"];

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "REPLACE_TICKETS") {
		return [...action.payload];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const { status, searchParam, showAll, selectedQueueIds, updateCount, style } =
			props;
	const classes = useStyles();
	const { whatsApps } = useWhatsApps();
	const ourPhones = React.useMemo(() => {
		const set = new Set();
		(whatsApps || []).forEach(w => { if (w && w.phoneNumber) set.add(w.phoneNumber); });
		return set;
	}, [whatsApps]);
	const [pageNumber, setPageNumber] = useState(1);
	const [refreshKey, setRefreshKey] = useState(0);
	const [snoozeClock, setSnoozeClock] = useState(0);
	const [pendingPreviewRevision, setPendingPreviewRevision] = useState(0);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const visibleTickets = React.useMemo(
		() => groupInternalTickets(
			ticketsList.map(withPendingMessagePreview),
			whatsApps
		),
		// El contador invalida la vista cuando cambia el almacén externo.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ticketsList, whatsApps, pendingPreviewRevision]
	);
	useEffect(
		() => onPendingMessage(() => {
			setPendingPreviewRevision(current => current + 1);
		}),
		[]
	);
	useEffect(() => {
		// snoozeClock fuerza este cálculo exactamente cuando vence un aplazamiento.
		const now = Date.now() + snoozeClock * 0;
		const nextExpiration = visibleTickets.reduce((nearest, ticket) => {
			const expiration = ticket?.snoozedUntil
				? new Date(ticket.snoozedUntil).getTime()
				: 0;
			if (expiration <= now) return nearest;
			return nearest === 0 || expiration < nearest ? expiration : nearest;
		}, 0);

		if (!nextExpiration) return undefined;

		const timeout = setTimeout(
			() => setSnoozeClock(current => current + 1),
			Math.min(nextExpiration - now + 50, 2147483647)
		);
		return () => clearTimeout(timeout);
	}, [visibleTickets, snoozeClock]);
	const { priorityTickets, otherTickets } = React.useMemo(() => {
		if (status !== "open") {
			return { priorityTickets: [], otherTickets: visibleTickets };
		}

		// La suma neutra registra el pulso como dependencia del orden calculado.
		const now = Date.now() + snoozeClock * 0;
		const priority = visibleTickets
			.filter(ticket =>
				ACTIVE_ATTENTION_STATUSES.indexOf(ticket.attentionStatus) > -1 &&
				!isTicketSnoozed(ticket, now)
			)
			.sort((a, b) => new Date(a.attentionSince) - new Date(b.attentionSince));
		const priorityIds = new Set(priority.map(ticket => ticket.id));
		const rest = visibleTickets.filter(ticket => !priorityIds.has(ticket.id));

		return { priorityTickets: priority, otherTickets: rest };
	}, [visibleTickets, status, snoozeClock]);
	const { user } = useContext(AuthContext);
	const reconnectRefreshTimeout = useRef(null);

	const requestListReload = useCallback(() => {
		setPageNumber(1);
		setRefreshKey(prevState => prevState + 1);
	}, []);

	const scheduleReconnectRefresh = useCallback(() => {
		if (reconnectRefreshTimeout.current) {
			clearTimeout(reconnectRefreshTimeout.current);
		}

		reconnectRefreshTimeout.current = setTimeout(() => {
			requestListReload();
			reconnectRefreshTimeout.current = null;
		}, 250);
	}, [requestListReload]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		refreshKey,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: pageNumber === 1 ? "REPLACE_TICKETS" : "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets, pageNumber, status, searchParam]);

	useEffect(() => {
		const socket = openSocket();

		const hasQueueFilter = selectedQueueIds.length > 0;

		const shouldUpdateTicket = ticket => !searchParam &&
			(!ticket.userId || ticket.userId === user?.id || showAll) &&
			(!hasQueueFilter || !ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToUserQueues = ticket =>
			hasQueueFilter && ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		const handleSocketConnect = () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
			scheduleReconnectRefresh();
		};

		const handleTicket = data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update" && status && data.ticket?.status !== status) {
				dispatch({
					type: "DELETE_TICKET",
					payload: data.ticket.id,
				});
				return;
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: data.ticket,
				});
			}

			if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		};

		const handleAppMessage = data => {
			if (data.action === "create") {
				resolvePendingMessage(data.message?._clientMessageId);
			}

			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
				});
			}
		};

		const handleContact = data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		};

		const handleTicketRefresh = event => {
			const { ticketId, status: nextStatus } = event?.detail || {};

			// La mutación ya fue confirmada por la API. Quitamos el ticket de
			// cualquier bandeja cuyo estado dejó de corresponder, sin depender
			// de que el evento de socket llegue antes que la navegación. El
			// socket ("ticket"/update y delete, emitidos por sala de estado en
			// UpdateTicketService) ya se encarga de sumarlo a la bandeja nueva
			// y de sincronizar cualquier otra pestaña abierta, así que aquí no
			// hace falta recargar la lista completa: eso reiniciaba la
			// paginación a la página 1 y le hacía perder al usuario la
			// posición donde iba con el scroll.
			if (ticketId && status && nextStatus && nextStatus !== status) {
				dispatch({ type: "DELETE_TICKET", payload: Number(ticketId) });
			}

			// Una navegación posterior tampoco debe poder recuperar la foto
			// anterior a la mutación desde otra clave o página de la caché.
			clearTicketsCache();
		};

		socket.on("connect", handleSocketConnect);
		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);
		socket.on("contact", handleContact);
		window.addEventListener(TICKETS_REFRESH_EVENT, handleTicketRefresh);

		return () => {
			if (reconnectRefreshTimeout.current) {
				clearTimeout(reconnectRefreshTimeout.current);
				reconnectRefreshTimeout.current = null;
			}
			window.removeEventListener(TICKETS_REFRESH_EVENT, handleTicketRefresh);
			socket.off("connect", handleSocketConnect);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
			socket.off("contact", handleContact);
			socket.disconnect();
		};
	}, [status, searchParam, showAll, user, selectedQueueIds, requestListReload, scheduleReconnectRefresh]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(visibleTickets.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTickets]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{visibleTickets.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{status === "open" && (
								<ListSubheader className={classes.attentionHeader}>
									{i18n.t("ticketsList.attentionRequired")}
								</ListSubheader>
							)}
							{status === "open" && priorityTickets.length === 0 && (
								<div className={classes.emptyAttentionTray}>
									<span className={classes.emptyAttentionText}>
										{i18n.t("ticketsList.noAttentionPending")}
									</span>
								</div>
							)}
							{priorityTickets.map(ticket => (
								<TicketListItem ticket={ticket} ourPhones={ourPhones} showAttentionActions key={ticket.id} />
							))}
							{status === "open" && (
								<ListSubheader className={classes.attendedHeader}>
									{i18n.t("ticketsList.attended")}
								</ListSubheader>
							)}
							{otherTickets.map(ticket => (
								<TicketListItem ticket={ticket} ourPhones={ourPhones} key={ticket.id} />
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
